import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Globe2,
  GraduationCap,
  MapPin,
  Radio,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { SEO } from "../components/SEO";

const platform = [
  {
    icon: BookOpen,
    year: "Year 1",
    label: "The Book",
    line: "An idea meets a manuscript, a manuscript meets a bindery, a bindery meets a hand.",
  },
  {
    icon: Radio,
    year: "Year 1–2",
    label: "The Audio",
    line: "Every title becomes a voice — audiobook, podcast, and long-form conversation.",
  },
  {
    icon: GraduationCap,
    year: "Year 2",
    label: "The Studio",
    line: "The author steps into the classroom. The book becomes a cohort course.",
  },
  {
    icon: Globe2,
    year: "Year 2–3",
    label: "The Translation",
    line: "The idea crosses a border, a language, a diaspora — and starts a second life.",
  },
  {
    icon: MapPin,
    year: "Year 3",
    label: "The District",
    line: "A district sponsors the book. A state library deposit-copies it. A story is preserved.",
  },
  {
    icon: Sparkles,
    year: "Year 3+",
    label: "The Screen",
    line: "Series, film, comic, syllabus. The book leaves the shelf and enters the culture.",
  },
];

const horizons = [
  { year: "2030", line: "Three thousand titles across nine imprints. A house that has stopped being small." },
  { year: "2047", line: "A hundred years of the republic. And a library built to hold what that century meant." },
  { year: "2100", line: "Books, bound today, still read by a reader we cannot yet imagine." },
];

const triangles = [
  { label: "The Reader", line: "Every CBH book is designed to be read at leisure, kept for a lifetime, and inherited by the next reader in the family." },
  { label: "The Writer", line: "We build long careers, not fast hits. A CBH author is signed for their next four books, not their next one." },
  { label: "The World", line: "We publish from Bharat, source in Bharat, and translate for the world. Every idea earns its way past every border." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About CBH — The Vision"
        path="/about"
        description="Not a publisher. A platform for ideas that outlast us. Conlang Book House builds a permanent archive of modern Bharat across six formats and three horizons."
        keywords="Conlang Book House vision, CBH platform, publishing house India, idea to impact"
      />
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16 grain-overlay" data-testid="about-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-8">§ The Vision</p>
          <h1 className="font-editorial text-6xl md:text-8xl lg:text-[9rem] leading-[0.88] tracking-tighter text-ink text-balance">
            Not a publisher. <br className="hidden md:block"/>
            <em className="text-crimson not-italic">A platform for ideas that outlast us.</em>
          </h1>
          <div className="mt-14 grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <p className="editorial-italic text-2xl md:text-3xl leading-snug text-ink-soft">
                CBH is not built to publish books. CBH is built to keep them alive — across
                formats, decades, borders, and generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VISION STATEMENT — large-scale, poetic */}
      <section className="bg-ink text-ivory grain-overlay py-24 lg:py-32" data-testid="about-vision-statement">
        <div className="mx-auto max-w-5xl px-6">
          <p className="ornament text-[11px] uppercase tracking-[0.32em] text-gold-light mb-10">The CBH Vision</p>
          <p className="font-editorial text-3xl md:text-5xl leading-[1.15] tracking-tight text-balance">
            To build the <em className="text-gold-light not-italic">most trusted archive</em> of
            modern Bharat &mdash; and to make sure the ideas inside it travel further than the
            people who wrote them.
          </p>
          <hr className="my-14 border-ivory/15" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold-light mb-3">The Belief</p>
              <p className="editorial-italic text-xl leading-snug text-ivory/85">A book is never just a book. It is a promise across time.</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold-light mb-3">The Refusal</p>
              <p className="editorial-italic text-xl leading-snug text-ivory/85">We do not chase the news cycle. We chase the archive.</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold-light mb-3">The Method</p>
              <p className="editorial-italic text-xl leading-snug text-ivory/85">Slow reporting. Restrained prose. Permanent binding.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM — the six-life of a CBH book */}
      <section className="py-24 lg:py-32" data-testid="about-platform">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16">
            <div className="col-span-12 lg:col-span-6">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ The Platform</p>
              <h2 className="font-editorial text-5xl md:text-7xl text-ink leading-[0.92] tracking-tight text-balance">
                One book. <em className="text-crimson not-italic">Six lives.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-lg leading-relaxed text-ink-muted">
                A publisher ships a book. A platform grows an idea. At CBH, every title is
                architected from day one to move across six formats over six years &mdash; from
                the hand-bound hardcover to the syllabus, the podcast, the district partnership,
                and, eventually, the screen.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-crimson/15">
            {platform.map(({ icon: Icon, year, label, line }, i) => (
              <div key={i} className="bg-ivory p-10 group cursor-default relative overflow-hidden">
                <div className="flex items-start justify-between mb-8">
                  <Icon strokeWidth={1} className="w-10 h-10 text-crimson" />
                  <span className="editorial-italic text-crimson/70 text-sm">{year}</span>
                </div>
                <p className="text-[10px] uppercase tracking-[0.32em] text-gold-dark mb-3">
                  Life {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-editorial text-3xl text-ink mb-4">{label}</h3>
                <p className="text-ink-muted leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIME HORIZONS — replaces the deleted stats strip with vision-forward horizons */}
      <section className="bg-ivory-soft grain-overlay py-24 lg:py-32" data-testid="about-horizons">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ The Horizons</p>
            <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight tracking-tight text-balance">
              We measure ourselves in <em className="text-crimson not-italic">decades.</em>
            </h2>
            <p className="mt-6 text-ink-muted text-lg">
              Publishing is a game of patience. CBH plans in fifteen-year arcs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {horizons.map(({ year, line }, i) => (
              <div key={i} className="relative border-l-2 border-crimson/40 pl-8 py-4">
                <p className="font-editorial text-7xl md:text-8xl text-crimson leading-none">{year}</p>
                <p className="mt-6 editorial-italic text-xl leading-snug text-ink-soft max-w-sm">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO — expanded, drop-cap editorial column */}
      <section className="py-24 lg:py-32" data-testid="about-manifesto">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ The Manifesto</p>
          <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-10 text-balance">
            Why we exist.
          </h2>
          <div className="text-xl leading-relaxed text-ink-soft space-y-6 dropcap">
            <p>
              Conlang Book House exists to publish books that outlast the moment they are
              written in. We do not chase the news cycle. We chase the archive.
            </p>
            <p>
              Founded to serve a country that has stories at every scale &mdash; from the
              boatman&rsquo;s son at Rameswaram to the youngest president of a national party
              &mdash; CBH is a modern publisher built on old principles: rigour in reporting,
              restraint in prose, permanence in binding.
            </p>
            <p>
              But we are not only a publisher. We are the infrastructure between an idea and
              the century it belongs to. A CBH title is a book, then an audiobook, then a
              course, then a translation, then a district partnership, then a screen. Six
              lives. One idea. One archive.
            </p>
            <p>
              We publish across four imprints, three tracks, and one very high bar. And we
              do so from Bharat, for the world.
            </p>
          </div>
        </div>
      </section>

      {/* READER / WRITER / WORLD TRIANGLE */}
      <section className="bg-ink text-ivory grain-overlay py-24 lg:py-32" data-testid="about-triangle">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-6">§ Our Three Audiences</p>
          <h2 className="font-editorial text-5xl md:text-7xl leading-[0.92] tracking-tight text-balance mb-16">
            Reader. Writer. <em className="text-gold-light not-italic">World.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ivory/10">
            {triangles.map((t, i) => (
              <div key={i} className="bg-ink p-10 lg:p-14">
                <p className="editorial-italic text-gold-light text-2xl mb-4">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-editorial text-3xl mb-6">{t.label}</h3>
                <p className="text-ivory/70 leading-relaxed">{t.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOUSE PRINCIPLES */}
      <section className="py-24 lg:py-32" data-testid="about-values">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ How We Publish</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-16 text-balance">
            The house <em className="text-crimson not-italic">principles.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {[
              ["Genre-Blend, Not Genre-Bind", "We tag books by the ideas they blend, not the shelf they were born on. A political biography can also be an economic history."],
              ["IP, Not Just ISBN", "Every title is tracked across twelve revenue surfaces — book, ebook, audiobook, film, course, translation, merchandise, and more."],
              ["Editorial Board, Not Algorithm", "No title enters the CBH list without unanimous editorial board approval. No exceptions."],
              ["Bharat First, World Ready", "We publish from India, source in India, and translate for the world. Every deluxe hardcover is bound in Delhi."],
            ].map(([t, d], i) => (
              <div key={i} className="border-l border-crimson/40 pl-8">
                <p className="editorial-italic text-crimson text-2xl mb-3">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-editorial text-2xl text-ink mb-4">{t}</h3>
                <p className="text-ink-muted leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-ivory-soft grain-overlay" data-testid="about-cta">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="ornament text-[11px] uppercase tracking-[0.32em] mb-8 justify-center">The Invitation</p>
          <h2 className="font-editorial text-5xl md:text-7xl text-ink leading-[0.95] tracking-tight text-balance">
            The archive is <em className="text-crimson not-italic">still being written.</em>
          </h2>
          <p className="mt-8 text-ink-muted text-lg max-w-2xl mx-auto">
            You could be reading it. You could be writing it. You could be underwriting a
            district that preserves it. Whichever door you come through &mdash; welcome.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/books"
              data-testid="about-cta-read"
              className="inline-flex items-center gap-3 bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500"
            >
              Read the Archive
              <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
            <Link
              to="/publish"
              data-testid="about-cta-publish"
              className="inline-flex items-center gap-3 border border-crimson/40 text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson/5 transition-colors duration-500"
            >
              Write for the Archive
            </Link>
            <Link
              to="/districts"
              data-testid="about-cta-underwrite"
              className="inline-flex items-center gap-3 border border-ink/25 text-ink px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-ink hover:text-ivory transition-colors duration-500"
            >
              Underwrite a District
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
